import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmResetPassword } from "aws-amplify/auth";
import { RESET_PASSWORD_UI } from "./confirmResetPassword.constants";
import {
  isPasswordPolicyInvalid,
  PASSWORD_POLICY_MESSAGE,
} from "../authFieldPolicy";

const INITIAL_FORM_VALUES = {
  code: "",
  password: "",
  confirmPassword: "",
};

function validateResetPassword(value) {
  if (!value) return RESET_PASSWORD_UI.passwordRequiredError;
  if (isPasswordPolicyInvalid(value)) return PASSWORD_POLICY_MESSAGE;
  return null;
}

function validateResetConfirmPassword(password, confirmPassword) {
  if (!confirmPassword) return RESET_PASSWORD_UI.confirmPasswordRequiredError;
  if (password !== confirmPassword) {
    return RESET_PASSWORD_UI.confirmPasswordMismatchError;
  }
  return null;
}

export function validateResetPasswordForm(values) {
  const errors = {};
  const code = String(values?.code ?? "").trim();

  if (!code) {
    errors.code = RESET_PASSWORD_UI.codeRequiredError;
  }

  const passwordError = validateResetPassword(values?.password);
  const confirmPasswordError = validateResetConfirmPassword(
    values?.password,
    values?.confirmPassword
  );

  if (passwordError) errors.password = passwordError;
  if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}

export function useConfirmResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const username = location.state?.username ?? "";

  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setBannerError("");
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleGoToSignIn = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const handleBackToApp = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const submit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      setBannerError("");
      setErrors({});

      if (!username) {
        setBannerError(RESET_PASSWORD_UI.sessionMissingOnSubmit);
        return;
      }

      const { errors: validationErrors, isValid } =
        validateResetPasswordForm(formValues);
      if (!isValid) {
        setErrors(validationErrors);
        return;
      }

      setLoading(true);
      try {
        await confirmResetPassword({
          username,
          confirmationCode: String(formValues.code ?? "").trim(),
          newPassword: formValues.password,
        });
        setSuccess(true);
      } catch (err) {
        setBannerError(
          err.message || RESET_PASSWORD_UI.submitErrorMessage
        );
      } finally {
        setLoading(false);
      }
    },
    [formValues, navigate, username]
  );

  const resetFields = useMemo(
    () =>
      RESET_PASSWORD_UI.resetFields.map((field) => ({
        ...field,
        disabled: loading,
      })),
    [loading]
  );

  return {
    username,
    formValues,
    errors,
    bannerError,
    success,
    loading,
    resetFields,
    handleChange,
    handleBack,
    handleGoToSignIn,
    handleBackToApp,
    submit,
    navigate,
  };
}
